import { useRef, useState } from "react";
import { UseFormMethods } from "react-hook-form";
import { googleTranslate } from "../googleTranslate";
import { LessonFormData, LocalizedText } from "../types";
import { readLocale, walkTranslatableFields } from "../utils";
import { ALL_LOCALES } from "../constants";

type TranslationJob = {
  /** Dot-path into the form, e.g. "parts.0.title" */
  path: string;
  locale: string;
  sourceText: string;
};

/**
 * Collect every LocalizedText field where the source locale has content but
 * one or more target locales are empty.
 */
const collectJobs = (data: LessonFormData, sourceLocale: string): TranslationJob[] => {
  const jobs: TranslationJob[] = [];
  const targetLocales = ALL_LOCALES.filter(l => l !== sourceLocale);

  for (const { path, value } of walkTranslatableFields(data.parts)) {
    const src = readLocale(value, sourceLocale).trim();
    if (!src) continue;
    for (const locale of targetLocales) {
      if (!readLocale(value, locale).trim()) {
        jobs.push({ path, locale, sourceText: src });
      }
    }
  }

  return jobs;
};

export const useAutoTranslateAll = (form: UseFormMethods<LessonFormData>, sourceLocale: string) => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const progressRef = useRef(0);

  const translateAll = async () => {
    const data = form.getValues();
    const jobs = collectJobs(data, sourceLocale);
    if (jobs.length === 0) return;

    setIsTranslating(true);
    progressRef.current = 0;
    setProgress({ done: 0, total: jobs.length });

    // Batch by locale so we can run locales in parallel, but sequential within
    // each locale to avoid hammering the free API too hard.
    const byLocale = new Map<string, TranslationJob[]>();
    for (const job of jobs) {
      const arr = byLocale.get(job.locale) ?? [];
      arr.push(job);
      byLocale.set(job.locale, arr);
    }

    const total = jobs.length;
    await Promise.all(
      Array.from(byLocale.entries()).map(async ([locale, localeJobs]) => {
        for (const job of localeJobs) {
          try {
            const translated = await googleTranslate(job.sourceText, sourceLocale, locale);
            // Re-read current value to avoid overwriting concurrent edits
            const current = form.getValues(job.path as any) as LocalizedText | undefined;
            // Only write if still empty (honour the "no overwrite" rule)
            if (current && !readLocale(current, locale).trim()) {
              form.setValue(job.path as any, { ...current, [locale]: translated }, { shouldDirty: true });
            }
          } catch {
            // Silently skip failed translations — user can retry
          }
          progressRef.current++;
          // Batch progress updates — only re-render every 5 translations or on the last one.
          if (progressRef.current % 5 === 0 || progressRef.current === total) {
            setProgress({ done: progressRef.current, total });
          }
        }
      })
    );

    setProgress({ done: total, total });
    setIsTranslating(false);
  };

  return { translateAll, isTranslating, progress };
};
